import posthog from 'posthog-js';

import { isLocalEnv, isProductionEnv } from './env';
import { maskAttributeFn, maskTextFn } from './replayMasking';
import { dropResizeObserverNoise, maskCapturedNetworkRequest, scrubPersonalData } from './posthogScrub';

const projectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

// Production and the dev deploy send; a build served from localhost or a loopback address never does,
// whatever its .env holds.
// Exactly 'dev': isDevEnv also matches the 'development' of a local .env.
// A tenant is told apart by the super properties registered on login.
const isLocalHost = isLocalEnv || ['127.0.0.1', '[::1]'].includes(window.location.hostname);
const isSendingEnv = (isProductionEnv || import.meta.env.VITE_ENV === 'dev') && !isLocalHost;

export const isPostHogEnabled = isSendingEnv && Boolean(projectToken && apiHost);

export const initPostHog = (): void => {
    if (!isSendingEnv) {
        return;
    }

    if (!projectToken || !apiHost) {
        const missingVariable = projectToken ? 'VITE_PUBLIC_POSTHOG_HOST' : 'VITE_PUBLIC_POSTHOG_PROJECT_TOKEN';
        console.warn(
            `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This warning stops appearing once ${missingVariable} is configured`
        );
        return;
    }

    posthog.init(projectToken, {
        api_host: apiHost,
        // api_host is our own proxy path, so links into the PostHog app need the real host.
        ui_host: 'https://eu.posthog.com',
        defaults: '2026-01-30',
        // Run in order on every outgoing event, including $pageview, $exception and autocapture; an
        // event the first one drops never reaches the scrubber.
        before_send: [dropResizeObserverNoise, scrubPersonalData],
        capture_exceptions: {
            capture_unhandled_errors: true,
            capture_unhandled_rejections: true,
            capture_console_errors: false,
        },

        // The recording quota is shared with production, so only production records.
        disable_session_recording: !isProductionEnv,
        session_recording: {
            maskAllInputs: true,
            // Routes every text node through maskTextFn, which is where the allowlist lives.
            maskTextSelector: '*',
            maskTextFn,
            maskAttributeFn,
            maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
        },
    });

    // A bare employee id is the same number in every tenant; drop it before the initial pageview goes out under it.
    if (/^\d+$/.test(posthog.get_distinct_id())) {
        posthog.reset();
    }
    registerDeploymentProperties();
};

// Registered before the initial pageview fires, so every event carries the deployment it came from.
// Must be re-registered after posthog.reset(), which wipes super properties.
export const registerDeploymentProperties = (): void => {
    posthog.register({
        env: import.meta.env.VITE_ENV,
        version: import.meta.env.VITE_GIT_COMMIT_HASH || 'unknown',
    });
};
